import { Channel } from '@nextforce/api'
import { Button, Input, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { usePolicies } from 'app/policies'
import { font } from 'app/theme'
import { Image } from 'expo-image'
import React, { useState } from 'react'
import { View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ButtonGroupComponent } from 'shared/components/button_group'
import { WebScreenParams } from 'web/types'

type Props = {
  phoneNumber?: string
  channel: Channel
  setPhoneNumber: (phoneNumber: string) => void
  setChannel: (channel: Channel) => void
  errorMessage?: any
  onLinkPress: (webParams: WebScreenParams) => void
  qrCodeUri?: string
  isQrExpired?: boolean
  timerSeconds?: number
  onSubmit: () => void
}

export default function VerifyView(props: Props) {
  const styles = useStyles()
  const policies = usePolicies()
  const [selectedIndex, setSelectedIndex] = useState<number>(
    props.channel === 'sms' ? 0 : 1
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View style={styles.card}>
          <Text style={[styles.title, styles.verificationText]}>
            {t('enterYourPhoneNumberForVerification')}
          </Text>
        </View>
        <View style={styles.inputContainer}>
          <Input
            inputStyle={styles.textInput}
            placeholderTextColor={styles.textInputPlaceholder.color}
            placeholder={t('mobileNumber')}
            value={props.phoneNumber}
            onChangeText={props.setPhoneNumber}
            maxLength={20}
            keyboardType="phone-pad"
            onSubmitEditing={props.onSubmit}
          />
        </View>
        <ButtonGroupComponent
          containerStyle={styles.channelButtonContainer}
          buttons={[t('textMe'), t('callMe')]}
          selectedIndex={selectedIndex}
          greyBackground={true}
          onPress={index => {
            setSelectedIndex(index)
            index === 0 ? props.setChannel('sms') : props.setChannel('call')
          }}
        />
        <Button
          containerStyle={styles.sendButtonContainer}
          titleStyle={styles.sendButtonTitle}
          title={t('sendVerificationCode')}
          onPress={props.onSubmit}
          disabled={!props.phoneNumber}
        />

        <Text style={styles.conditionsText}>
          {t('verificationSMSFees')} {t('signUpAgreement')}{' '}
          <Text
            style={styles.link}
            onPress={() => props.onLinkPress(policies.termsOfUse)}
          >
            {policies.termsOfUse.title}
          </Text>{' '}
          and{' '}
          <Text
            style={styles.link}
            onPress={() => props.onLinkPress(policies.privacy)}
          >
            {policies.privacy.title}
          </Text>
          .
        </Text>
        {props.qrCodeUri ? (
          <View style={styles.qrContainer}>
            <Text>{t('qrCodeDescription')}</Text>
            <View>
              <Image
                style={styles.qr}
                source={{ uri: props.qrCodeUri }}
                contentFit="contain"
              />
              <Text style={[styles.conditionsText, styles.counterText]}>
                {t('refreshCounter', { count: props.timerSeconds })}
              </Text>
              {props.isQrExpired ? (
                <Text style={[styles.conditionsText, styles.counterText]}>
                  {t('qrCodeExpiredRegenerating')}
                </Text>
              ) : null}
            </View>
          </View>
        ) : null}
        {props.errorMessage ? (
          <Text style={styles.error}>{props.errorMessage}</Text>
        ) : null}
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors?.grey5,
    alignItems: 'center',
  },
  inputContainer: {
    flexDirection: 'row',
    backgroundColor: 'white',
    paddingHorizontal: 10,
    paddingVertical: 15,
    width: 350,
    marginTop: 10,
    marginBottom: 10,
    borderRadius: 4,
    alignItems: 'center',
  },
  textInput: {
    fontSize: 20,
    color: 'grey',
    paddingHorizontal: 10,
    width: '100%',
    textAlign: 'center',
  },
  textInputPlaceholder: {
    color: 'lightgray',
  },
  spacer: {
    margin: 4,
  },
  error: {
    margin: 10,
    textAlign: 'center',
    color: theme.colors?.primary,
    fontFamily: font.bold,
    fontSize: 16,
  },
  card: {
    backgroundColor: theme.colors?.grey5,
    paddingHorizontal: 40,
  },
  title: {
    textAlign: 'center',
    margin: 10,
    fontSize: 18,
    color: theme.colors?.grey1,
  },
  verificationText: {
    textAlign: 'left',
  },
  label: {
    margin: 10,
    fontSize: 15,
    color: theme.colors?.grey1,
  },
  conditionsText: {
    marginHorizontal: 40,
    marginTop: 8,
  },
  link: {
    textDecorationLine: 'underline',
  },
  channelButtonContainer: {
    width: 350,
    marginTop: 6,
  },
  sendButtonContainer: {
    width: 350,
    borderRadius: 8,
    marginTop: 20,
    marginBottom: 10,
  },
  sendButtonTitle: {
    fontFamily: font.bold,
  },
  qrContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  qr: { width: 200, height: 200 },
  counterText: {
    marginHorizontal: 0,
    width: 200,
    textAlign: 'center',
  },
}))
